import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { Button } from 'antd-v4'
import { message } from 'antd'
import { THEME_COLORS } from '@getgreenline/shared'
import { syncOrganizationProducts } from './organizationsApi'
import { parseErrorMsg } from '../../../utilities/helpers'

interface Props {
  organizationId: number
}

/**
 * Confirmation is rendered inline rather than through Popconfirm or Modal.confirm. Both are
 * portal-based and misbehave from inside this Drawer: Popconfirm crashed in rc-align when the
 * trigger re-rendered as the popup closed, and an antd v3 Modal renders beneath the v4 Drawer's
 * mask (both default to z-index 1000). Inline markup has no mask, no z-index and no alignment.
 */
export const OrgProductSyncSection = ({ organizationId }: Props) => {
  const [isConfirming, setIsConfirming] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const startSync = async () => {
    setIsSyncing(true)
    try {
      const { companyCount, productCount } = await syncOrganizationProducts(organizationId)
      message.success(
        `Sync started — ${productCount} product${
          productCount === 1 ? '' : 's'
        } across ${companyCount} compan${companyCount === 1 ? 'y' : 'ies'}`,
      )
    } catch (error) {
      if (isMountedRef.current) {
        message.error(parseErrorMsg(error) || 'Failed to start product sync')
      }
    } finally {
      if (isMountedRef.current) {
        setIsSyncing(false)
        setIsConfirming(false)
      }
    }
  }

  return (
    <div className='mb-3'>
      <div style={styles.header}>
        <span style={styles.headerTitle}>Products</span>
      </div>

      <div style={styles.description}>
        Re-sends every product in this organization, including deleted ones, to OpenSearch. Use it
        when org products are missing from search after a migration. Nothing is modified.
      </div>

      {isConfirming ? (
        <div style={styles.confirmRow}>
          <span style={styles.confirmText}>Re-send every product to OpenSearch?</span>
          <div style={styles.confirmButtons}>
            <Button size='small' disabled={isSyncing} onClick={() => setIsConfirming(false)}>
              Cancel
            </Button>
            <Button size='small' type='primary' loading={isSyncing} onClick={startSync}>
              Sync
            </Button>
          </div>
        </div>
      ) : (
        <Button onClick={() => setIsConfirming(true)}>Sync products</Button>
      )}
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  headerTitle: {
    fontWeight: 600,
  },
  description: {
    color: THEME_COLORS.MONO.mono77,
    fontSize: 13,
    marginBottom: 8,
  },
  confirmRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '8px 12px',
    border: `1px solid ${THEME_COLORS.MONO.monoee}`,
    borderRadius: 4,
    background: THEME_COLORS.MONO.monoFa,
  },
  confirmText: {
    fontSize: 13,
    fontWeight: 500,
  },
  confirmButtons: {
    display: 'flex',
    gap: 8,
  },
}
